/**
 * canvasType — the single source of truth for CANVAS-rendered text sizes
 * (BL-77), the in-engine parallel to `ui/uiTokens.ts` §A (which governs DOM
 * text).
 *
 * Canvas text is drawn into the game field, which scales by `CanvasScaler` + DPR
 * — a different pipeline from the DOM stage — so CSS `--ui-scale` can't reach it.
 * Instead {@link canvasFont} multiplies the role size by {@link getUiScale} from
 * the same single source the DOM uses, so both worlds move together.
 *
 * The tiny bold game-field labels keep `'Courier New'` on purpose: it hints
 * better than VT323 at small px on a busy, moving field (VT323 stays the DOM
 * brand). The boot title pixel-font (`BootPixelFont`, 150px VT323) is bespoke
 * art and is NOT routed through here.
 *
 * Floor: `fieldLabel` (9px) — the minimum readable game-field label size; the
 * purely decorative `fieldNoise` glyphs may sit below it.
 */
import { getUiScale } from "../utils/uiScale.ts";

export type CanvasRoleName =
  | "fieldLabel"
  | "fieldNoise"
  | "damage"
  | "ghostBadge"
  | "banner"
  | "warningEdge";

export interface CanvasTypeRole {
  /** Base size in logical px @ `--ui-scale: 1` (before DPR + uiScale). */
  size: number;
  /** Font weight prefix (`"bold "` or `""`). */
  weight: "bold " | "";
  /** Font-family tail of the `ctx.font` string. */
  family: string;
}

const COURIER = "'Courier New', monospace";

/**
 * The canvas type roles. Sizes are logical px (DPR is applied globally by the
 * render pipeline's `setTransform`; do NOT pre-multiply DPR here).
 */
export const CANVAS_TYPE: Record<CanvasRoleName, CanvasTypeRole> = {
  /** Game-field labels: SEALED · SPAWN · MAINFRAME · UNTARGETABLE · BREACH. */
  fieldLabel: { size: 9, weight: "bold ", family: COURIER },
  /** Decorative hex/debris glyphs — purely aesthetic, may sit below the floor. */
  fieldNoise: { size: 7, weight: "", family: COURIER },
  /** Floating damage numbers (variant scale ×1/×1.3/×1.8 passed as `mult`). */
  damage: { size: 16, weight: "bold ", family: COURIER },
  /** Tower-placement / payload-aim ghost badge. */
  ghostBadge: { size: 12, weight: "bold ", family: COURIER },
  /** GameBanner wave/boss announcements. Retained for callers that still pass
   *  a custom role, but GameBanner itself now renders every message through
   *  `warningEdge` (D-331 — the grid re-route: only urgent gameplay warnings
   *  still land on the board, and they land small/at-the-edge, not centred). */
  banner: { size: 32, weight: "bold ", family: "monospace" },
  /** D-331 — the discreet edge-warning surface GameBanner now renders through:
   *  small, corner-docked, legible without dominating the board. Sized against
   *  `fieldLabel` (9px, the smallest field text) rather than the old 32px
   *  centred `banner` role — a warning still reads clearly, it just no longer
   *  competes with the play grid for the player's eye. */
  warningEdge: { size: 15, weight: "bold ", family: COURIER },
} as const;

const cache = new Map<string, string>();

/**
 * Build a `ctx.font` string for a canvas role, scaled by the global `uiScale`
 * and an optional per-call multiplier (e.g. crit/synergy damage variants, or a
 * pop-in tween). Results are cached by the rounded final size so hot loops
 * (damage numbers, particles) don't re-concat every frame.
 */
export function canvasFont(role: CanvasRoleName, mult = 1): string {
  const r = CANVAS_TYPE[role];
  const px = Math.max(1, Math.round(r.size * getUiScale() * mult));
  const key = `${role}:${px}`;
  let s = cache.get(key);
  if (s === undefined) {
    s = `${r.weight}${px}px ${r.family}`;
    cache.set(key, s);
  }
  return s;
}

/** The final rounded px a role renders at right now (for layout math). */
export function canvasFontSize(role: CanvasRoleName, mult = 1): number {
  return Math.max(1, Math.round(CANVAS_TYPE[role].size * getUiScale() * mult));
}
